import * as React from "react";
import {Box, FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {TransactionalMailTemplate} from "crqlar-react-common/dist/models/newsletter/transactionalMailTemplate.model";
import {LocalizedMailTemplate} from "crqlar-react-common/dist/models/sector/sector.model";
import {getOrganizationLanguages} from "crqlar-react-common/dist/utils/useSupportedLanguages";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";

interface LocalizedMailTemplateFieldProps {
    value?: LocalizedMailTemplate;
    onChange: (value: LocalizedMailTemplate) => void;
    mailTemplates: TransactionalMailTemplate[];
    /** Rendered next to the language toggle; receives the currently selected language. */
    actions?: (selectedLanguage: string) => React.ReactNode;
}

const LocalizedMailTemplateField: React.FC<LocalizedMailTemplateFieldProps> = ({
    value,
    onChange,
    mailTemplates,
    actions
}) => {
    const {t} = useTranslation();
    const organization = useOrganization();
    const supportedLanguages = getOrganizationLanguages(organization);

    const [selectedLanguage, setSelectedLanguage] = React.useState<string>(
        supportedLanguages[0] || 'en'
    );

    const handleLanguageChange = (
        _event: React.MouseEvent<HTMLElement>,
        newLanguage: string | null
    ) => {
        if (newLanguage !== null) {
            setSelectedLanguage(newLanguage);
        }
    };

    const selectedValue = value?.[selectedLanguage] ?? '';

    const handleTemplateChange = (templateId: string) => {
        const updatedValue = {...(value ?? {})};
        if (templateId) {
            updatedValue[selectedLanguage] = templateId;
        } else {
            delete updatedValue[selectedLanguage];
        }
        onChange(updatedValue);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: '100%',
                mt: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                }}
            >
                <ToggleButtonGroup
                    value={selectedLanguage}
                    exclusive
                    onChange={handleLanguageChange}
                    size="small"
                    sx={{gap: 0.5, flexShrink: 0}}
                >
                    {supportedLanguages.map((lang) => (
                        <ToggleButton
                            key={lang}
                            value={lang}
                            sx={{
                                px: 1,
                                py: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <img
                                alt={getNameForLanguage(lang)}
                                style={{width: '16px', height: '16px'}}
                                src={getIconForLanguage(lang)}
                            />
                            <span style={{fontSize: '0.875rem'}}>
                                {lang.toUpperCase()}
                            </span>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {!!actions && (
                    <Box sx={{display: 'flex', alignItems: 'center', ml: 'auto'}}>
                        {actions(selectedLanguage)}
                    </Box>
                )}
            </Box>

            <FormControl
                fullWidth
                size="small"
                sx={{
                    minWidth: {xs: 0, sm: '300px'},
                }}
            >
                <InputLabel>
                    {t('Select template for {{language}}', {language: getNameForLanguage(selectedLanguage, true)})}
                </InputLabel>
                <Select
                    value={selectedValue}
                    label={t('Select template for {{language}}', {language: getNameForLanguage(selectedLanguage, true)})}
                    onChange={(e) => handleTemplateChange(e.target.value as string)}
                >
                    <MenuItem value="">
                        <em>{t('No template selected')}</em>
                    </MenuItem>
                    {mailTemplates.map((tmpl) => (
                        <MenuItem key={tmpl.id} value={tmpl.id}>
                            {tmpl.name}
                            {tmpl.fromEmail && (
                                <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                                    ({tmpl.fromEmail})
                                </Typography>
                            )}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

export default LocalizedMailTemplateField;
